import { createSlice } from '@reduxjs/toolkit';

export interface AgentAssistItem {
  text: string;
  index: number;
}

export interface AgentAssistSuggestion {
  conversationId: string;
  messageId?: string;
  // Legacy single-suggestion field kept for backwards compat with old socket events
  suggestion?: string;
  // New multi-suggestion fields
  items: AgentAssistItem[];
  sentiment: 'positive' | 'negative' | 'neutral';
  urgency: 'high' | 'medium' | 'low';
  confidence?: number;
  receivedAt: number;
}

interface AgentAssistState {
  suggestion: AgentAssistSuggestion | null;
  pendingInsert: string | null;
  // Urgency persisted per conversation so the ring shows in the list across nav
  conversationUrgency: Record<string, 'high' | 'medium' | 'low'>;
}

const initialState: AgentAssistState = {
  suggestion: null,
  pendingInsert: null,
  conversationUrgency: {},
};

const agentAssistSlice = createSlice({
  name: 'agentAssist',
  initialState,
  reducers: {
    setAgentAssistSuggestion(state, action: { payload: AgentAssistSuggestion }) {
      const p = action.payload;
      if (!p.items || p.items.length === 0) {
        state.suggestion = {
          ...p,
          items: p.suggestion ? [{ text: p.suggestion, index: 0 }] : [],
          sentiment: p.sentiment ?? 'neutral',
          urgency: p.urgency ?? 'low',
        };
      } else {
        state.suggestion = p;
      }
      // Persist urgency keyed by conversationId so the nav ring survives navigation
      if (p.conversationId && p.urgency) {
        state.conversationUrgency[p.conversationId] = p.urgency;
      }
    },
    clearSuggestion(state) {
      state.suggestion = null;
    },
    setPendingInsert(state, action: { payload: string }) {
      state.pendingInsert = action.payload;
    },
    clearPendingInsert(state) {
      state.pendingInsert = null;
    },
    clearConversationUrgency(state, action: { payload: string }) {
      delete state.conversationUrgency[action.payload];
    },
  },
});

export const {
  setAgentAssistSuggestion,
  clearSuggestion,
  setPendingInsert,
  clearPendingInsert,
  clearConversationUrgency,
} = agentAssistSlice.actions;
export default agentAssistSlice.reducer;
